import type { TelemetryEvent } from "./bus";

/**
 * AN1 (D-122): what one page load did, for the record its close sends (`page_left`): where the
 * page stood, the games it started and ended, and how long it was in view. Read from the events
 * the page records, so neither the front page nor the game tells it anything; the caller gives
 * the time (ms since the page loaded) and the page's visibility.
 *
 * Where: "front" before any game (the front page, or a link's game still loading), "table" seated
 * at a duel's table before its start, "match" in a game, "end" on a game's end screen.
 */
type Left = Extract<TelemetryEvent, { type: "page_left" }>;

export type Place = Left["where"];

export interface Visit {
  /** One of the page's telemetry events. */
  observe(event: TelemetryEvent): void;
  /** The page came into view, or left it, at `at`. */
  shown(visible: boolean, at: number): void;
  /** The record of the page's close at `at`. */
  left(at: number): Left;
}

const PLACE_AFTER: Partial<Record<TelemetryEvent["type"], Place>> = {
  lobby_joined: "table",
  lobby_left: "front",
  match_started: "match",
  run_end: "end",
};

export function createVisit(options: { readonly visible: boolean; readonly team: boolean }): Visit {
  let where: Place = "front";
  let games = 0;
  let ended = 0;
  let inViewMs = 0;
  let shownAt: number | undefined = options.visible ? 0 : undefined;
  return {
    observe(event) {
      where = PLACE_AFTER[event.type] ?? where;
      if (event.type === "match_started") {
        games += 1;
      } else if (event.type === "run_end") {
        ended += 1;
      }
    },
    shown(visible, at) {
      if (visible && shownAt === undefined) {
        shownAt = at;
      } else if (!visible && shownAt !== undefined) {
        inViewMs += at - shownAt;
        shownAt = undefined;
      }
    },
    left(at) {
      const visibleMs = inViewMs + (shownAt === undefined ? 0 : at - shownAt);
      return {
        type: "page_left",
        where,
        games,
        ended,
        visible_ms: Math.round(visibleMs),
        ...(options.team ? { team: true } : {}),
      };
    },
  };
}
